import { Container, Typography } from '@cleartrip/ct-design-components'
import React from 'react'

import { CabinTabId, FareCabinTabId, getCabinTabLabel } from '@/constants/flights/cabinClass'
import {
    getCabinInterstitialTheme,
} from '@/constants/flights/premiumEconomy'
import { formatCurrency } from '@/utils'

type CabinTabRowProps = {
    selectedCabin: CabinTabId
    onSelect: (cabin: CabinTabId) => void
    isRT: boolean
    onPeTabRef?: (node: HTMLElement | null) => void
    /** BE-driven tab subtitle price (cabinClassSummary[cabin].minCabinPrice). */
    priceByCabin?: Partial<Record<CabinTabId, number>>
    /**
     * The cabins to actually render, in order. May include sub-products
     * (e.g. INDIGO_UPFRONT) outside the four theme cabins.
     */
    visibleCabins: CabinTabId[]
    /**
     * Cabin whose theme chrome the strip wears (from fares' cabinType).
     * Separate from selectedCabin so Indigo Upfront keeps Economy look.
     */
    themeCabin?: FareCabinTabId
    /** Tab the caption caret points at — PE by default, Indigo Upfront for that upsell. */
    caretTabId?: CabinTabId
}

const PREMIUM_ECONOMY = 'PREMIUM_ECONOMY'

const CabinTabRow: React.FC<CabinTabRowProps> = ({
    selectedCabin,
    onSelect,
    isRT: _isRT,
    onPeTabRef,
    priceByCabin,
    visibleCabins,
    themeCabin,
    caretTabId = PREMIUM_ECONOMY,
}) => {
    const themeKey = (themeCabin ||
        (selectedCabin as FareCabinTabId)) as Parameters<typeof getCabinInterstitialTheme>[0]
    const {
        activeTabBorderColor,
        inactiveTabTextColor = '#808080',
        tabDividerColor = '#E6E6E6',
    } = getCabinInterstitialTheme(themeKey)
    const activeTabTextColor = '#1A1A1A'

    // Tabs only make sense when there is a choice. A single available cabin
    // drops the tab row entirely — mirrors flights-pwa.
    if (visibleCabins.length <= 1) return null

    return (
        <Container className="p-relative w-100p">
            <Container
                aria-hidden
                className="p-absolute w-100p"
                css={{
                    bottom: 0,
                    left: 0,
                    height: '1px',
                    zIndex: 0,
                    backgroundColor: tabDividerColor,
                }}
            />
            <Container className="flex p-relative pt-2 px-6 flex-gap-3">
                {visibleCabins.map(key => {
                    const isActive = selectedCabin === key
                    const bePrice = priceByCabin?.[key]
                    const tabPriceLabel = bePrice != null ? `at ${formatCurrency(bePrice)}` : ''
                    const textColor = isActive ? activeTabTextColor : inactiveTabTextColor
                    return (
                        <Container
                            key={key}
                            ref={key === caretTabId ? onPeTabRef : undefined}
                            className={`flex flex-column flex-middle px-2 py-2 c-pointer${
                                isActive ? ' bg-white' : ''
                            }`}
                            css={{
                                width: '160px',
                                boxSizing: 'border-box',
                                position: 'relative',
                                zIndex: isActive ? 2 : 1,
                                borderTop: isActive ? `1px solid ${activeTabBorderColor}` : '0',
                                borderLeft: isActive ? `1px solid ${activeTabBorderColor}` : '0',
                                borderRight: isActive ? `1px solid ${activeTabBorderColor}` : '0',
                                borderBottom: isActive ? '1px solid #FFFFFF' : '0',
                                borderTopLeftRadius: isActive ? '16px' : '12px',
                                borderTopRightRadius: isActive ? '16px' : '12px',
                                marginBottom: isActive ? '-1px' : '0',
                                backgroundColor: isActive ? '#FFFFFF' : undefined,
                                boxShadow: isActive ? '0 1px 0 0 #FFFFFF' : undefined,
                            }}
                            onClick={() => onSelect(key)}
                        >
                            <Typography className="ta-center" colorCode={textColor} variant="HM4">
                                {getCabinTabLabel(key)}
                            </Typography>
                            {tabPriceLabel && (
                                <Typography className="ta-center mt-1" colorCode={textColor} variant="B3">
                                    {tabPriceLabel}
                                </Typography>
                            )}
                        </Container>
                    )
                })}
            </Container>
        </Container>
    )
}

export default CabinTabRow
